import axios from 'axios';

// Mirrors AiAssistant/Models/Contracts.cs on the backend (B2C.NewConcept.V23/AiAssistant).
export interface AssistantResultRef {
  resultId: string;
  viewType: string;
  displayMode: string;
  dataVersion: number;
}

// Command type for clicking a control on a hotel card (HotelCardActionCommand on the backend).
export const HOTEL_CARD_ACTION = 'hotel-card-action';

export const HOTEL_CARD_ACTIONS = [
  'show-rooms',
  'pin',
  'center-me',
  'www',
  'focus',
  'room-offers',
  'add-to-proposal',
  'open-preferred',
  'open-refund',
  'click-price',
  'filter-rooms',
] as const;

export type HotelCardActionKind = (typeof HOTEL_CARD_ACTIONS)[number];

// Payload of HOTEL_CARD_ACTION. The page finds the hotel (and room when needed) and runs the same click handler.
export interface HotelCardActionPayload {
  // Which card / rooms-panel control to click (see HOTEL_CARD_ACTIONS).
  action: HotelCardActionKind;
  // Hotel name as shown on the card / list.
  hotelName?: string | null;
  // Optional exact page hotel key (htuKey).
  hotelKey?: string | null;
  // 1-based position in the visible results list (#1 / first / #3).
  index?: number | null;
  // Room display name when the action targets a room (open-refund / click-price / add-to-proposal).
  roomName?: string | null;
  // Optional exact room key (rcUniqueKey).
  roomKey?: string | null;
  // For action=pin: true pins, false unpins; omit/null toggles.
  pinned?: boolean | null;
  // For action=show-rooms: true opens, false closes; omit/null toggles.
  show?: boolean | null;
  // filter-rooms: rooms search box text ("" clears).
  roomFilterName?: string | null;
  // filter-rooms: Free checkbox (refundable / free cancellation).
  roomRefundableOnly?: boolean | null;
  // filter-rooms: wheelchair / accessible checkbox.
  roomAccessibleOnly?: boolean | null;
  // filter-rooms: meal-included only (not RO).
  roomBoardingOnly?: boolean | null;
  // filter-rooms: meal chips BB/HB/FB/AI ([] clears).
  roomMealPlans?: string[] | null;
  // filter-rooms: CXL sort checkbox.
  roomSortByCxl?: boolean | null;
  // filter-rooms: reset the whole rooms filter bar.
  clearRoomFilters?: boolean | null;
}

// Command type for clicking a control on a flight result card.
export const FLIGHT_CARD_ACTION = 'flight-card-action';

export const FLIGHT_CARD_ACTIONS = ['add-to-proposal', 'select', 'info'] as const;

export type FlightCardActionKind = (typeof FLIGHT_CARD_ACTIONS)[number];

export interface FlightCardActionPayload {
  action: FlightCardActionKind;
  // 1-based position in the visible results list (#1 / first / #3).
  index?: number | null;
  flightId?: string | null;
  flightKey?: string | null;
  flightName?: string | null;
  // For add-to-proposal: true adds, false removes; omit/null toggles.
  added?: boolean | null;
  // For info: true opens, false closes; omit/null toggles.
  show?: boolean | null;
}

export const PROPOSAL_ACTION = 'proposal-action';
export const PROPOSAL_ACTION_ALIASES = [
  PROPOSAL_ACTION,
  'proposal_action',
  'proposalAction',
] as const;

export const isProposalActionCommand = (type?: string | null) =>
  !!type &&
  PROPOSAL_ACTION_ALIASES.some((alias) => alias.toLowerCase() === type.trim().toLowerCase());

export const PROPOSAL_ACTIONS = [
  'page-tab',
  'cancellation-delta',
  'show-cancellation-date',
  'show-price',
  'clear-all',
  'create',
  'save',
  'comment',
  'language',
  'template',
  'sort',
  'currency',
  'remove',
  'edit',
  'select-room',
  'show-rooms',
  'filter-rooms',
  'hotel-tab',
  'edit-price',
  'free-text',
  'share',
  'source-check',
  'load-source',
] as const;

export type ProposalActionKind = (typeof PROPOSAL_ACTIONS)[number];

export type ProposalActionProduct = 'hotels' | 'flights' | 'dynamic' | 'transfers' | 'packages';

export interface ProposalActionPayload {
  action: ProposalActionKind;
  tab?: string | null;
  value?: number | null;
  text?: string | null;
  // comment: replace (default) or append to the existing proposal note.
  mode?: 'replace' | 'append' | null;
  checked?: boolean | null;
  show?: boolean | null;
  product?: ProposalActionProduct | null;
  hotelName?: string | null;
  roomName?: string | null;
  // 1-based room positions for select-room (e.g. [1,2,3,4] or a "1-4" range).
  roomIndexes?: number[] | null;
  index?: number | null;
  priceType?: 'p' | 'v' | 'e' | null;
  priceValue?: number | null;
  applyAll?: boolean | null;
  language?: string | null;
  template?: string | null;
  sort?: string | null;
  currency?: string | null;
  channel?: 'email' | 'pdf' | 'whatsapp' | 'telegram' | null;
  email?: string | null;
  // Proposal owner name (Additional info → Name). Required to create.
  ownerName?: string | null;
  // Additional info (create): optional expire date yyyy-MM-dd / dd-MMM-yyyy.
  expireDate?: string | null;
  // Additional info (create): optional proposal password.
  password?: string | null;
  // Additional info (create): cancellation date delta 0-5.
  cancellationDateDelta?: number | null;
  // Additional info (create): Show cancellation date checkbox.
  showCancellationDate?: boolean | null;
  // Additional info (create): Display total checkbox.
  displayTotalDelta?: boolean | null;
  // Additional info (create): proposal image URL when already known.
  proposalImage?: string | null;
  source?: 'google' | 'tripadvisor' | 'info' | 'ai' | null;
  field?: 'reviews' | 'details' | 'amenities' | 'overview' | 'prosCons' | 'images' | null;
  imageIndex?: number | null;
  part?: 'hotel' | 'flight' | 'ticket' | null;
  roomFilterName?: string | null;
  roomRefundableOnly?: boolean | null;
  roomAccessibleOnly?: boolean | null;
  roomBoardingOnly?: boolean | null;
  roomMealPlans?: string[] | null;
  roomSortByCxl?: boolean | null;
  clearRoomFilters?: boolean | null;
}

export interface CreateSessionResponse {
  sessionId: string;
  status: string;
  // Whether to show the microphone: voice input is on, this user's branch is in the pilot and the speech-to-text vendor
  // has a key. voiceMaxSeconds is where the recorder stops by itself. An older backend omits both: no microphone.
  voiceEnabled?: boolean;
  voiceMaxSeconds?: number;
  // The model that answers in this session. The backend only sends it to BePro staff.
  aiModel?: string | null;
}

// Something the assistant asks the page to do besides answering in the chat (AssistantUiCommand on the
// backend). A page runs the types it knows and ignores the rest.
export interface AssistantUiCommand {
  type: string;
  payload?: Record<string, unknown> | null;
}

// Command type for opening a hotel search on the results page.
export const OPEN_HOTEL_SEARCH = 'open-hotel-search';

// Payload of OPEN_HOTEL_SEARCH (OpenHotelSearchCommand on the backend). `query` is the results page's own
// URL query, one string per parameter, named as FE.hotel's SearchHotelModel reads them. `googlePlace` is what
// the page's destination box keeps in sessionStorage. `appliedFilters` is applied once the new search has hotels.
// `keepFilters` mirrors the results toolbar "search with active filters" lock: current filters survive the new search.
export interface OpenHotelSearchPayload {
  query: Record<string, string>;
  googlePlace?: Record<string, unknown> | null;
  appliedFilters?: Record<string, unknown> | null;
  keepFilters?: boolean | null;
  keep_filters?: boolean | null;
  searchWithActiveFilters?: boolean | null;
}

// Command type for opening a flight search on the results page.
export const OPEN_FLIGHT_SEARCH = 'open-flight-search';

// Payload of OPEN_FLIGHT_SEARCH. Prefer SearchFlightModel field names in `query`; common aliases are also accepted.
// Mandatory: destination (arrivalCode / destination / to / …). Optional (defaulted on FE when absent):
// origin, dates, travelers, cabineType, searchType, directFlight, isGds, isNDC, isLowCost,
// flightSourcesDefault (NONE/ALP/ABOX), numberOfBags, additionalFrom/To (+ labels) for B2B round-trip.
// `appliedFilters` is applied once the new search has flights (stops, baggage, price, times, …).
export interface OpenFlightSearchPayload {
  query: Record<string, string>;
  appliedFilters?: Record<string, unknown> | null;
}

// Command type for running the My Orders search. Fields may sit on the payload or inside `query`.
// Absent fields stay as they are; null / "" clears that field. An empty payload presses GO.
export const SEARCH_ORDERS = 'search-orders';
export const SEARCH_ORDERS_ALIASES = [
  SEARCH_ORDERS,
  'search_orders',
  'searchOrders',
  'open-orders-search',
  'open_orders_search',
] as const;

export const isSearchOrdersCommand = (type?: string | null) =>
  !!type &&
  SEARCH_ORDERS_ALIASES.some((alias) => alias.toLowerCase() === type.trim().toLowerCase());

// Command type for a My Orders row: toolbar and tab actions, or selecting the active row by id or #n.
export const ORDER_ACTION = 'order-action';
export const ORDER_ACTION_ALIASES = [
  ORDER_ACTION,
  'order_action',
  'orderAction',
  'orders-action',
  'select-order',
  'select_order',
  'selectOrder',
  'set-active-order',
] as const;

export const isOrderActionCommand = (type?: string | null) =>
  !!type &&
  ORDER_ACTION_ALIASES.some((alias) => alias.toLowerCase() === type.trim().toLowerCase());

// Command type for changing the filters of the results page that is already open.
export const APPLY_HOTEL_FILTERS = 'apply-hotel-filters';
export const APPLY_HOTEL_FILTERS_ALIASES = [
  APPLY_HOTEL_FILTERS,
  'set-filters',
  'set_filters',
  'setFilters',
] as const;

export const isApplyHotelFiltersCommand = (type?: string | null) =>
  !!type &&
  APPLY_HOTEL_FILTERS_ALIASES.some(
    (alias) => alias.toLowerCase() === type.trim().toLowerCase(),
  );

// Payload of APPLY_HOTEL_FILTERS. Same shape as appliedFilters in the message response; a reset arrives as
// cleared values (empty `stars`, null `maxPrice`, false flags) so the page drops its own limits too.
export interface ApplyHotelFiltersPayload {
  appliedFilters: Record<string, unknown>;
}

export const APPLY_FLIGHT_FILTERS = 'apply-flight-filters';
export const APPLY_FLIGHT_FILTERS_ALIASES = [
  APPLY_FLIGHT_FILTERS,
  'set-flight-filters',
  'set_flight_filters',
  'setFlightFilters',
] as const;

export const isApplyFlightFiltersCommand = (type?: string | null) =>
  !!type &&
  APPLY_FLIGHT_FILTERS_ALIASES.some(
    (alias) => alias.toLowerCase() === type.trim().toLowerCase(),
  );

export interface ApplyFlightFiltersPayload {
  appliedFilters: Record<string, unknown>;
}

export const SET_RESULT_LOCATION = 'set-result-location';

export interface SetResultLocationPayload {
  clear?: boolean | null;
  name?: string | null;
  lat?: number | null;
  lng?: number | null;
  km?: number | null;
}

export interface SendMessageResponse {
  sessionId: string;
  messageId: string;
  message: string;
  result?: AssistantResultRef | null;
  appliedFilters?: Record<string, unknown> | null;
  uiActions: string[];
  uiCommands?: AssistantUiCommand[];
  // The model that wrote this answer. The backend only sends it to BePro staff.
  aiModel?: string | null;
  // False for a reply the server wrote itself (too long, a limit, a busy session): it is not stored, so it cannot be
  // rated. Absent from an older backend, which means it can.
  feedbackAllowed?: boolean;
}

export interface AssistantResultResponse {
  resultId: string;
  viewType: string;
  displayMode: string;
  dataVersion: number;
  data: Record<string, unknown> | null;
  expired: boolean;
}

// Minimal snapshot of the open results URL / search (PageSearchContext on the backend). When set, NewConcept
// binds a page-only search so set_filters can emit apply-hotel-filters without a prior chat search.
export interface PageSearchContext {
  searchToken?: string | null;
  checkIn?: string | null;
  checkOut?: string | null;
  city?: string | null;
  lat?: string | null;
  lon?: string | null;
  countryCode?: string | null;
  loc?: string | null;
  roomsString?: string | null;
  nationality?: string | null;
  searchRadiusKm?: number | null;
}

// Which app page the agent UI is showing (SendMessageRequest.OpenPage on the backend).
export type AssistantOpenPage =
  | 'hotel-results'
  | 'proposal'
  | 'flights'
  | 'my-orders'
  | 'hotels';

// Local-only override so a developer can point the assistant calls at a backend running on
// their own machine (e.g. the "AiAssistantDev" launch profile) without touching the app's normal
// baseURL, which stays pointed wherever it's already configured for every other request. Set via
// a gitignored .env.local, e.g. REACT_APP_ASSISTANT_BASE_URL=http://localhost:5226/backend
const LOCAL_BASE_URL = process.env.REACT_APP_ASSISTANT_BASE_URL || undefined;

export const createAssistantSession = async (locale?: string): Promise<CreateSessionResponse> =>
  (await axios.post('assistant/sessions', { locale }, { baseURL: LOCAL_BASE_URL })).data;

export const sendAssistantMessage = async (
  sessionId: string,
  text: string,
  context?: {
    searchId?: string;
    appliedFilters?: unknown;
    pageSearch?: PageSearchContext | null;
    openPage?: AssistantOpenPage | null;
  },
): Promise<SendMessageResponse> =>
  (
    await axios.post(
      `assistant/sessions/${sessionId}/messages`,
      {
        text,
        searchId: context?.searchId,
        appliedFilters: context?.appliedFilters,
        pageSearch: context?.pageSearch,
        openPage: context?.openPage,
      },
      { baseURL: LOCAL_BASE_URL },
    )
  ).data;

// The text of a recorded voice message (VoiceTranscribeResponse on the backend). The page sends it through
// sendAssistantMessage like typed text. A failure comes back as a reason instead of a throw so the chat can say what
// happened: 429 = a voice limit, 403 = voice is not available to this user.
export type VoiceTranscription =
  | { ok: true; text: string; language?: string | null }
  | { ok: false; reason: 'limit' | 'not_available' | 'failed' };

const voiceFileExtension = (mimeType: string) => {
  if (mimeType.includes('mp4')) return 'm4a';
  if (mimeType.includes('ogg')) return 'ogg';
  if (mimeType.includes('wav')) return 'wav';
  return 'webm';
};

export const transcribeAssistantVoice = async (audio: Blob): Promise<VoiceTranscription> => {
  const form = new FormData();
  form.append('audio', audio, `voice.${voiceFileExtension(audio.type)}`);
  try {
    // The multipart type has to be named here: the app sets a global JSON content type at login (services/user.ts), and
    // with it axios sends a FormData as JSON ({"audio":{}}), the recording lost. Named, axios keeps the FormData and the
    // browser adds the boundary.
    const res = await axios.post('assistant/voice/transcribe', form, {
      baseURL: LOCAL_BASE_URL,
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return { ok: true, text: res.data?.text ?? '', language: res.data?.language ?? null };
  } catch (err) {
    const status = axios.isAxiosError(err) ? err.response?.status : undefined;
    return { ok: false, reason: status === 429 ? 'limit' : status === 403 ? 'not_available' : 'failed' };
  }
};

export const getAssistantResult = async (resultId: string): Promise<AssistantResultResponse> =>
  (await axios.get(`assistant/results/${resultId}`, { baseURL: LOCAL_BASE_URL })).data;

// A user's rating of one assistant answer. Three optional Yes/No answers plus free text; score is derived
// (any No -> -1, all Yes -> +1, none answered -> 0). Sending again for the same answer replaces the earlier one.
export interface AssistantFeedbackPayload {
  messageId: string;
  score: number;
  understood?: boolean | null;
  correct?: boolean | null;
  helped?: boolean | null;
  reason?: string;
}

export const sendAssistantFeedback = async (
  sessionId: string,
  payload: AssistantFeedbackPayload,
): Promise<{ feedbackId: string }> =>
  (
    await axios.post(`assistant/sessions/${sessionId}/feedback`, payload, {
      baseURL: LOCAL_BASE_URL,
    })
  ).data;

// ---- Session Viewer (BePro staff only). The backend answers 403 to anyone else.

export interface AssistantAdminSessionQuery {
  companyId?: string;
  departmentId?: number;
  branchId?: number;
  userId?: string;
  status?: string;
  // Whole days, yyyy-MM-dd. `to` is included.
  from?: string;
  to?: string;
  // A session id (or its first characters), or a word the conversation contains.
  text?: string;
  // Only sessions in which at least one answer was rated or commented on.
  hasFeedback?: boolean;
  page?: number;
  pageSize?: number;
}

export interface AssistantAdminFilterOption {
  id: number;
  name: string;
}

export interface AssistantAdminBranchOption extends AssistantAdminFilterOption {
  departmentId?: number | null;
}

export interface AssistantAdminUserOption {
  companyId: string;
  userId: string;
  name: string;
  departmentId?: number | null;
  branchId?: number | null;
}

// What the viewer's filters can choose from: only what has sessions.
export interface AssistantAdminFilters {
  companies: string[];
  departments: AssistantAdminFilterOption[];
  branches: AssistantAdminBranchOption[];
  users: AssistantAdminUserOption[];
  statuses: string[];
}

export interface AssistantAdminSessionRow {
  sessionId: string;
  companyId: string;
  userId: string;
  userName?: string | null;
  departmentId?: number | null;
  departmentName?: string | null;
  branchId?: number | null;
  branchName?: string | null;
  locale?: string | null;
  status: string;
  searchId?: string | null;
  createdAt: string;
  lastActivityAt: string;
  messageCount: number;
  firstUserMessage?: string | null;
  // Ratings given in the session: liked, disliked, and comment-only (score 0). Absent from an older backend.
  feedbackUp?: number;
  feedbackDown?: number;
  feedbackNeutral?: number;
  // Tokens sent to / received from the model over the whole session. Absent from an older backend.
  promptTokens?: number;
  completionTokens?: number;
}

export interface AssistantAdminSessionList {
  items: AssistantAdminSessionRow[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AssistantAdminMessage {
  messageId: string;
  role: 'user' | 'assistant' | 'system' | 'tool' | string;
  text?: string | null;
  aiModel?: string | null;
  promptTokens?: number | null;
  completionTokens?: number | null;
  totalTokens?: number | null;
  costUsd?: number | null;
  createdAt: string;
  // The user's rating of this answer; absent when it was not rated.
  feedback?: AssistantAdminFeedback | null;
}

// 1 = liked, -1 = disliked, 0 = a comment without a rating (the backend clamps a score to -5..5).
export interface AssistantAdminFeedback {
  score: number;
  understood?: boolean | null;
  correct?: boolean | null;
  helped?: boolean | null;
  reason?: string | null;
  userId: string;
  userName?: string | null;
  createdAt: string;
}

export interface AssistantAdminToolCall {
  toolLogId: string;
  toolName: string;
  input?: unknown;
  status: string;
  durationMs?: number | null;
  errorCode?: string | null;
  errorMessage?: string | null;
  createdAt: string;
}

export interface AssistantAdminSessionDetail {
  session: AssistantAdminSessionRow;
  messages: AssistantAdminMessage[];
  toolCalls: AssistantAdminToolCall[];
  totalTokens: number;
}

export const getAssistantAdminFilters = async (): Promise<AssistantAdminFilters> =>
  (await axios.get('assistant/admin/filters', { baseURL: LOCAL_BASE_URL })).data;

export const listAssistantAdminSessions = async (
  query: AssistantAdminSessionQuery,
): Promise<AssistantAdminSessionList> =>
  (await axios.get('assistant/admin/sessions', { baseURL: LOCAL_BASE_URL, params: query })).data;

export const getAssistantAdminSession = async (
  sessionId: string,
): Promise<AssistantAdminSessionDetail> =>
  (await axios.get(`assistant/admin/sessions/${sessionId}`, { baseURL: LOCAL_BASE_URL })).data;
